import styled from 'styled-components';
import { CALLER_COLORS as C } from '../tokens';

// Shared styled-components for the Caller Detail panel (Figma node 19:4515).
// Font-family inherited (app-wide Poppins); only the design's sizes/weights/colors set.

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const Scroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: ${C.panelBg};
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const SectionLabel = styled.div`
  color: ${C.gray};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 15px;
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 12px;
`;

/* CUSTOMER */
// Plain (non-editable) customer row — used when the caller has no real customerId.
export const CustomerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
// The whole customer row is one large touch target that opens the edit modal.
// Padding enlarges the hit area; the negative side margin keeps the avatar aligned
// with the section label while the active state gives touch feedback.
export const CustomerRowButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
  padding: 6px 8px;
  margin: 0 -8px;
  background: transparent;
  border: none;
  border-radius: 10px;
  text-align: left;
  cursor: pointer;
  &:active {
    background: ${C.lightBtnBg};
  }
`;
export const Avatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: ${C.avatarBg};
  flex-shrink: 0;
  img {
    width: 14px;
    height: 14px;
  }
`;
export const CustomerName = styled.span`
  color: ${C.navy};
  font-size: 20px;
  font-weight: 700;
`;
export const CustomerPhone = styled.span`
  color: ${C.gray};
  font-size: 18px;
`;
// Visual edit affordance (icon chip) after the phone — the row is the actual button,
// so this is a non-interactive span to avoid nesting a button inside a button.
export const EditPencil = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${C.lightBtnBg};
  border: 1px solid ${C.border};
  img {
    width: 16px;
    height: 16px;
  }
`;

/* HISTORY */
// Tighter inter-entry spacing than the shared Card gap, so the preview stays compact.
export const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;
export const HistoryEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
`;
export const HistoryTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;
export const HistoryText = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  color: ${C.navy};
  font-size: 14px;
  line-height: 1.3;
  /* Highlighted visit date — bold navy pill, distinct from the orange Re Book action. */
  .date {
    background: ${C.lightBtnBg};
    border: 1px solid ${C.border};
    color: ${C.navy};
    font-weight: 700;
    padding: 1px 8px;
    border-radius: 6px;
  }
  .ser {
    color: ${C.gray};
  }
`;
export const HistoryNote = styled.div`
  color: ${C.gray};
  font-size: 12px;
  line-height: 1.3;
  .label {
    font-weight: 600;
  }
`;
export const RebookChip = styled.button`
  flex-shrink: 0;
  background: ${C.orangeSoftBg};
  border: 1px solid ${C.accentOrange};
  color: ${C.accentOrange};
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  padding: 5px 11px;
  cursor: pointer;
  &:active {
    opacity: 0.7;
  }
`;

export const CenterRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: 2px;
`;
export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: ${C.lightBtnBg};
  border: 1px solid ${C.border};
  border-radius: 999px;
  padding: 8px 18px;
  color: ${C.slate};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:active {
    opacity: 0.7;
  }
  img {
    width: 16px;
    height: 16px;
  }
`;

/* NOTES */
export const NoteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
export const Bullet = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: ${C.accentOrange};
  flex-shrink: 0;
`;
export const NoteText = styled.span`
  color: ${C.navy};
  font-size: 15px;
`;
